/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges} from '@angular/core';

import type {FileUploadHandler} from '../../common/directives/file-upload/file-upload.util';
import template from './release-note-asset-upload.component.html';
import {CdnAssetResource} from '../../../components/api/resources/resource/asset/cdn-asset.resource';

interface ReleaseNoteAsset {
  cdnId: string;
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  width: number;
  height: number;
  imgString: string;
}

const UPLOAD_ACCEPT = '.webp,.jpg,.png,.jpeg,.gif,.svg';

@Component({
  standalone: false,
  selector: 'adn-release-note-asset-upload',
  template
})
export class ReleaseNoteAssetUploadComponent implements OnChanges {
  @Input() assetStrings: string[] = [];
  @Output() assetStringsChange = new EventEmitter<string[]>();

  readonly uploadAccept = UPLOAD_ACCEPT;
  readonly warningOptions = {mode: 'cdn-asset' as const};
  showUploader = false;
  assets: ReleaseNoteAsset[] = [];

  constructor(@Inject(CdnAssetResource) private readonly CdnAsset: any) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.assetStrings) {
      this.refreshAssets();
    }
  }

  trackAsset(_index: number, asset: ReleaseNoteAsset): string {
    return asset.id || asset.cdnId || String(_index);
  }

  uploadCdnFile: FileUploadHandler = (file, onProgress) => {
    return this.CdnAsset.upload(null, {file: file}, onProgress).then(
      (asset: any) => asset,
      (error: any) => Promise.reject(error)
    );
  };

  toggleUploader(): void {
    this.showUploader = !this.showUploader;
  }

  onAssetUploaded(asset: any): void {
    const nextAssetString = [
      asset.cdnId,
      asset.id,
      asset.fileName,
      asset.mimeType,
      asset.fileSizeBytes,
      asset.width,
      asset.height
    ].join('{}');

    this.assetStrings = this.assetStrings || [];
    this.assetStrings.push(nextAssetString);
    this.refreshAssets();
    this.assetStringsChange.emit(this.assetStrings);
  }

  private refreshAssets(): void {
    this.assets = (this.assetStrings || []).map((assetString) => this.parseAssetString(assetString));
  }

  private parseAssetString(assetString: string): ReleaseNoteAsset {
    const assetSplit = (assetString || '').split('{}');
    const asset = {
      cdnId: assetSplit[0] || '',
      id: assetSplit[1] || '',
      fileName: assetSplit[2] || '',
      mimeType: assetSplit[3] || '',
      fileSizeBytes: Number(assetSplit[4]) || 0,
      width: Number(assetSplit[5]) || 0,
      height: Number(assetSplit[6]) || 0,
      imgString: ''
    };
    asset.imgString = `<img src='${asset.cdnId}' width='${asset.width}' height='${asset.height}'>`;
    return asset;
  }
}
